<script setup>
/**
 * Step one of campaign creation: which channel. One card per channel, each
 * carrying its own availability — a channel whose prerequisite is missing stays
 * visible but disabled with the reason, so both the channel and what unlocks it
 * are discoverable. A channel the account does not have at all (WhatsApp behind
 * its feature flag) is never in `channels` to begin with.
 */
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  // Descriptors from the page: { value, label, description, icon,
  // isAvailable, unavailableReason }.
  channels: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

// An unavailable card stays in the tab order rather than using native
// `disabled`, so keyboard and screen reader users still reach it and hear its
// prerequisite. That means the guard against selecting one lives here.
const onSelect = channel => {
  if (!channel.isAvailable) return;
  emit('select', channel.value);
};

const reasonId = channel => `channel-step-reason-${channel.value}`;
</script>

<template>
  <div
    role="group"
    :aria-label="t('CAMPAIGN.GALLERY.CHANNEL_STEP.GROUP_LABEL')"
    class="grid grid-cols-1 gap-3 sm:grid-cols-3"
  >
    <button
      v-for="channel in channels"
      :key="channel.value"
      type="button"
      :aria-disabled="!channel.isAvailable"
      :aria-describedby="channel.isAvailable ? undefined : reasonId(channel)"
      class="flex flex-col gap-2 p-4 text-left transition-shadow rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:shadow-md focus-visible:outline-2 focus-visible:outline-n-brand aria-disabled:opacity-60 aria-disabled:cursor-default aria-disabled:hover:shadow-none min-h-32"
      @click="onSelect(channel)"
    >
      <Icon :icon="channel.icon" class="size-5 text-n-slate-11" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ channel.label }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ channel.description }}
      </span>
      <span
        v-if="!channel.isAvailable"
        :id="reasonId(channel)"
        class="mt-auto text-xs font-medium text-n-amber-11"
      >
        {{ channel.unavailableReason }}
      </span>
    </button>
  </div>
</template>
